import React from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { useCurrencyFormatter } from "../../../Components/Hooks/useCurrencyFormatter";

const Tile = ({ label, value, color, meta, progress }) => (
  <div style={{ background: "#fff", padding: "16px 20px" }}>
    <div className="text-uppercase text-muted" style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: 0.6 }}>
      {label}
    </div>
    <div style={{ fontSize: 23, fontWeight: 650, marginTop: 6, color, fontVariantNumeric: "tabular-nums" }}>
      {value}
    </div>
    {progress != null ? (
      <div className="d-flex align-items-center gap-2 mt-2">
        <div className="flex-grow-1" style={{ height: 5, borderRadius: 3, background: "#eceaf1", overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${progress}%`, background: "#1a9c5f", borderRadius: 3 }} />
        </div>
        <span style={{ fontSize: 12, fontWeight: 600, color: "var(--vz-secondary)" }}>{progress}%</span>
      </div>
    ) : (
      <div className="text-muted mt-1" style={{ fontSize: 12.5 }}>
        {meta}
      </div>
    )}
  </div>
);

// Four summary tiles computed client-side from the contracts already loaded
// by getClientDetails (ContractForClientSerializer) — no extra endpoint.
const ClientDetailStats = ({ contracts }) => {
  const { formatCurrency } = useCurrencyFormatter();
  const list = contracts || [];

  const totalContracted = list.reduce((a, c) => a + (c.total_contract_value || 0), 0);
  const paidToDate = list.reduce((a, c) => a + (c.total_cashed || 0), 0);
  const outstanding = totalContracted - paidToDate;
  const paidPct = totalContracted > 0 ? Math.round((paidToDate / totalContracted) * 100) : 0;

  const unpaidPayments = list.flatMap((c) =>
    (c.payments || [])
      .filter((p) => !p.cashed)
      .map((p) => ({ ...p, pvc_number: c.pvc_number }))
  );
  const nextPayment = unpaidPayments.sort((a, b) => {
    if (!a.booking_date) return 1;
    if (!b.booking_date) return -1;
    return new Date(a.booking_date) - new Date(b.booking_date);
  })[0];

  const tiles = [
    {
      label: "Total contracted",
      value: formatCurrency(totalContracted, "EUR"),
      meta: `across ${list.length} contract${list.length === 1 ? "" : "s"}`,
    },
    {
      label: "Paid to date",
      value: formatCurrency(paidToDate, "EUR"),
      color: "#146c43",
      progress: paidPct,
    },
    {
      label: "Outstanding",
      value: formatCurrency(outstanding, "EUR"),
      meta: `${unpaidPayments.length} payment${unpaidPayments.length === 1 ? "" : "s"} remaining`,
    },
    nextPayment
      ? {
          label: "Next payment due",
          value: nextPayment.booking_date ? moment(nextPayment.booking_date).format("D MMM YYYY") : "Unscheduled",
          meta: `${formatCurrency(nextPayment.amount, "EUR")} · Contract #${nextPayment.pvc_number}`,
        }
      : { label: "Next payment due", value: "—", meta: "fully settled" },
  ];

  return (
    <div
      className="mb-4"
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
        gap: 1,
        background: "var(--vz-border-color)",
        border: "1px solid var(--vz-border-color)",
        borderRadius: 14,
        overflow: "hidden",
      }}
    >
      {tiles.map((t) => (
        <Tile key={t.label} {...t} />
      ))}
    </div>
  );
};

ClientDetailStats.propTypes = {
  contracts: PropTypes.array,
};

export default ClientDetailStats;
